import { useContext, useEffect, useState } from 'react'
import * as XLSX from 'xlsx'
import MasivaContent from './MasivaContent'
import IndividualContent from './IndividualContent'
import ResizableModal from '../../../../../ui/ResizableModal'
import { Button, Title } from '../../../../../ui'
import GlobalContext from '../../../../../../../context/global-context'
import { BusinessAPI } from '../../../../../../../api/business.api'
import { sileo } from 'sileo'
import { currentYear } from '../../../../../../utils/constant'

const RUT_FORMAT_REGEX = /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/
const BUK_RUT_FORMAT_REGEX = /^\d{7,8}-[\dkK]$/
const VALID_MONTHS = new Set([
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre'
])
const TEXT_COLUMN_HEADERS = new Set(['mes', 'nombre', 'rut', 'cargo'])
const EXCEL_FILE_EXTENSIONS = new Set(['xlsx', 'xls'])
const BUK_FILE_EXTENSIONS = new Set([...EXCEL_FILE_EXTENSIONS, 'csv'])
const XLSX_FILE_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
/*
const REMUNERATION_TOTALS_COLUMN_INDEX = 1
const REMUNERATION_TOTALS_LABEL = 'Totales'
const REMUNERATION_EXCEL_HEADERS = [
  'Mes',
  'Nombre',
  'RUT',
  'DiasTrab.',
  'Cargo',
  'Sueldo Base',
  'Gratificación',
  'Otros Imponibles  (bonos, comisiones, Horas Extras, Aguinaldo, Semana Corrida)',
  'Total imponible',
  'Moviliz',
  'Colac',
  'Otros no Imponibles',
  'Total No Imponible',
  'Total Haberes',
  'Cotización AFP',
  'Cotización Salud',
  'Adicional Isapre',
  'Seguro Cesantía',
  'APV Regimen B',
  'Impuesto Unico',
  'Retenc. 3%',
  'AFP Cuenta2',
  'APV Regimen A',
  'Anticipo Sueldo (Quincena)',
  'Pago Prestamos Personal',
  'Pago Prestamos instituciones',
  'Otros Descuentos',
  'Total Descuentos',
  'Sueldo Líquido',
  'Seg. Cesantía Empleador',
  'SIS Empleador',
  'Seg. Acc Empleador',
  'Aporte Patronal L. Sociales'
]
*/

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()

const normalizeHeader = (header) =>
  normalizeText(header)
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase()

const getFileExtension = (file) =>
  String(file?.name || '')
    .split('.')
    .pop()
    .toLowerCase()

const isCsvFile = (file) => getFileExtension(file) === 'csv'

const getAllowedFileExtensions = (source) =>
  source === 'buk' ? BUK_FILE_EXTENSIONS : EXCEL_FILE_EXTENSIONS

const getAllowedFileFormatLabel = (source) =>
  source === 'buk' ? 'Excel (.xlsx o .xls) o CSV (.csv)' : 'Excel (.xlsx o .xls)'

const getMassiveUploadFileError = (source, file) => {
  if (!file) return null

  const extension = getFileExtension(file)
  if (getAllowedFileExtensions(source).has(extension)) return null

  return `Formato no valido. Para ${source === 'buk' ? 'Buk' : 'remuneraciones'} debes subir un archivo ${getAllowedFileFormatLabel(source)}.`
}

const readWorkbookFromFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      try {
        resolve(XLSX.read(new Uint8Array(event.target.result), { type: 'array' }))
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => reject(new Error('No se pudo leer el archivo'))
    reader.readAsArrayBuffer(file)
  })

const readRowsFromFile = async (file) => {
  const workbook = await readWorkbookFromFile(file)
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  return XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true })
}

const getRowsFromSheet = (sheet) =>
  XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true })

/*
const getHeaderRowFromSheet = (sheet, expectedHeaderCount) => {
  if (!sheet?.['!ref']) return Array(expectedHeaderCount).fill('')

  const range = XLSX.utils.decode_range(sheet['!ref'])
  const lastColumnIndex = Math.max(range.e.c, expectedHeaderCount - 1)

  return Array.from({ length: lastColumnIndex + 1 }, (_, columnIndex) => {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: columnIndex })
    return String(sheet[cellAddress]?.v ?? '').trim()
  })
}

const validateRemunerationHeaders = (sheet) => {
  const errors = []
  const fileHeaders = getHeaderRowFromSheet(sheet, REMUNERATION_EXCEL_HEADERS.length)

  REMUNERATION_EXCEL_HEADERS.forEach((expectedHeader, index) => {
    const currentHeader = fileHeaders[index] || ''
    if (currentHeader === expectedHeader) return

    errors.push(
      `Fila 1, columna ${XLSX.utils.encode_col(index)}: se esperaba "${expectedHeader}" y se encontro "${currentHeader || '(vacio)'}".`
    )
  })

  fileHeaders.slice(REMUNERATION_EXCEL_HEADERS.length).forEach((header, offset) => {
    if (!header) return

    const columnIndex = REMUNERATION_EXCEL_HEADERS.length + offset
    errors.push(
      `Fila 1, columna ${XLSX.utils.encode_col(columnIndex)}: columna no permitida "${header}". Usa solo las columnas de la plantilla.`
    )
  })

  return errors
}

const getLastNonEmptyCellInColumn = (sheet, columnIndex, startRowIndex = 1) => {
  if (!sheet?.['!ref']) return null

  const range = XLSX.utils.decode_range(sheet['!ref'])

  for (let rowIndex = range.e.r; rowIndex >= startRowIndex; rowIndex--) {
    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })
    const value = String(sheet[cellAddress]?.v ?? '').trim()

    if (value) {
      return {
        rowIndex,
        value
      }
    }
  }

  return null
}

const validateRemunerationTotalsRow = (sheet) => {
  const lastTotalsCell = getLastNonEmptyCellInColumn(sheet, REMUNERATION_TOTALS_COLUMN_INDEX)
  const columnName = XLSX.utils.encode_col(REMUNERATION_TOTALS_COLUMN_INDEX)

  if (!lastTotalsCell) {
    return [
      `Columna ${columnName}: la ultima fila con datos debe decir "${REMUNERATION_TOTALS_LABEL}".`
    ]
  }

  if (lastTotalsCell.value !== REMUNERATION_TOTALS_LABEL) {
    return [
      `Fila ${lastTotalsCell.rowIndex + 1}, columna ${columnName}: la ultima fila con datos en la columna ${columnName} debe decir "${REMUNERATION_TOTALS_LABEL}" y se encontro "${lastTotalsCell.value}".`
    ]
  }

  return []
}
*/

const getFileNameWithoutExtension = (fileName) =>
  String(fileName || '')
    .replace(/\.[^/.]+$/, '')
    .trim()

const convertCsvToExcelFile = async (file) => {
  const workbook = await readWorkbookFromFile(file)
  const xlsxData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const fileName = `${getFileNameWithoutExtension(file.name) || 'BUK'}.xlsx`

  return new File([xlsxData], fileName, { type: XLSX_FILE_MIME_TYPE })
}

const getBukUploadFile = (file) =>
  isCsvFile(file) ? convertCsvToExcelFile(file) : Promise.resolve(file)

const isValidRutFormat = (rut, formatRegex = RUT_FORMAT_REGEX) =>
  formatRegex.test(String(rut || '').trim())

const isNumericCellValue = (value) => {
  if (value === '' || value === null || value === undefined) return true
  if (typeof value === 'number') return Number.isFinite(value)

  const normalized = String(value)
    .trim()
    .replace(/\s+/g, '')
    .replace(/\./g, '')
    .replace(/,/g, '.')

  return /^-?\d+(\.\d+)?$/.test(normalized)
}

const isValidRutCheckDigit = (rut) => {
  const clean = String(rut).replace(/\./g, '').toUpperCase()
  const [number, dv] = clean.split('-')
  if (!number || !dv || dv.length !== 1) return false

  let sum = 0
  let multiplier = 2

  for (let i = number.length - 1; i >= 0; i--) {
    sum += Number(number[i]) * multiplier
    multiplier = multiplier === 7 ? 2 : multiplier + 1
  }

  const expected = 11 - (sum % 11)
  const expectedDv = expected === 11 ? '0' : expected === 10 ? 'K' : String(expected)

  return expectedDv === dv
}

const validateRutColumns = (
  row,
  rowNumber,
  {
    formatRegex = RUT_FORMAT_REGEX,
    expectedFormatLabel = '12.345.678-9'
  } = {}
) => {
  const errors = []

  Object.entries(row).forEach(([header, value]) => {
    const normalizedHeader = normalizeHeader(header)
    if (!normalizedHeader.includes('rut')) return

    const rawValue = String(value ?? '').trim()
    if (!rawValue) return

    if (!isValidRutFormat(rawValue, formatRegex)) {
      errors.push(
        `Fila ${rowNumber}, columna "${header}": "${rawValue}" - formato incorrecto, debe ser ${expectedFormatLabel}`
      )
    } else if (!isValidRutCheckDigit(rawValue)) {
      errors.push(
        `Fila ${rowNumber}, columna "${header}": "${rawValue}" - digito verificador incorrecto`
      )
    }
  })

  return errors
}

const validateRemunerationExcel = async (file) => {
  try {
    const workbook = await readWorkbookFromFile(file)
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    const errors = []

    /*
    errors.push(...validateRemunerationHeaders(sheet))
    if (errors.length > 0) return errors

    errors.push(...validateRemunerationTotalsRow(sheet))

    if (errors.length > 0) return errors
    */

    const rows = getRowsFromSheet(sheet)
    let expectedMonth = null
    let expectedMonthLabel = null

    rows.forEach((row, idx) => {
      const rowNumber = idx + 2
      const rowEntries = Object.entries(row)
      const rowHasContent = rowEntries.some(([, value]) => String(value ?? '').trim() !== '')
      if (!rowHasContent) return

      const monthEntry = rowEntries.find(([header]) => normalizeHeader(header) === 'mes')
      const monthHeader = monthEntry?.[0] || 'Mes'
      const monthValue = String(monthEntry?.[1] ?? '').trim()
      const normalizedMonthValue = normalizeText(monthValue).toLowerCase()

      if (!monthValue) {
        errors.push(`Fila ${rowNumber}, columna "${monthHeader}": debe indicar un mes`)
      } else if (!VALID_MONTHS.has(normalizedMonthValue)) {
        errors.push(`Fila ${rowNumber}, columna "${monthHeader}": "${monthValue}" - mes no valido`)
      } else if (!expectedMonth) {
        expectedMonth = normalizedMonthValue
        expectedMonthLabel = monthValue
      } else if (normalizedMonthValue !== expectedMonth) {
        errors.push(
          `Fila ${rowNumber}, columna "${monthHeader}": "${monthValue}" - debe coincidir con el mes "${expectedMonthLabel}" en todas las filas`
        )
      }

      rowEntries.forEach(([header, value]) => {
        const normalizedHeader = normalizeHeader(header)
        if (!normalizedHeader) return

        const rawValue = String(value ?? '').trim()
        if (!rawValue) return

        if (normalizedHeader === 'mes') {
          return
        }

        if (normalizedHeader === 'rut') {
          errors.push(
            ...validateRutColumns(
              { [header]: value },
              rowNumber,
              {
                formatRegex: RUT_FORMAT_REGEX,
                expectedFormatLabel: '12.345.678-9'
              }
            )
          )
          return
        }

        if (TEXT_COLUMN_HEADERS.has(normalizedHeader)) {
          return
        }

        if (!isNumericCellValue(value)) {
          errors.push(
            `Fila ${rowNumber}, columna "${header}": "${rawValue}" - debe contener solo valores numericos`
          )
        }
      })
    })

    return errors
  } catch {
    return []
  }
}

const validateBukExcel = async (file) => {
  try {
    const rows = await readRowsFromFile(file)
    const errors = []

    rows.forEach((row, idx) => {
      const rowNumber = idx + 2
      errors.push(
        ...validateRutColumns(
          row,
          rowNumber,
          {
            formatRegex: BUK_RUT_FORMAT_REGEX,
            expectedFormatLabel: '12345678-9'
          }
        )
      )
    })

    return errors
  } catch {
    return []
  }
}

const REMUNERATION_REPLACE_ALL_NULL_ERROR_PATTERN =
  /TypeError:\s*Cannot read properties of null\s*\(reading ['"]replaceAll['"]\)/i

const getRemunerationUploadRawMessage = (source) => {
  const rawMessage =
    typeof source === 'string'
      ? source
      : source?.response?.data?.message ||
        source?.response?.data?.error ||
        (typeof source?.response?.data === 'string' ? source.response.data : '') ||
        source?.data?.message ||
        source?.data?.error ||
        (typeof source?.data === 'string' ? source.data : '') ||
        source?.message ||
        ''

  return String(rawMessage).replace(/[\r\n]+/g, ' ').trim()
}

const isRemunerationReplaceAllNullError = (message) =>
  REMUNERATION_REPLACE_ALL_NULL_ERROR_PATTERN.test(String(message || ''))

const isRemunerationUploadErrorMessage = (message) =>
  /^error:/i.test(message) || isRemunerationReplaceAllNullError(message)

const getRemunerationUploadErrorMessage = (error) => {
  const normalizedMessage = getRemunerationUploadRawMessage(error) || 'Error al subir archivo'

  if (isRemunerationReplaceAllNullError(normalizedMessage)) {
    return 'La carga masiva contiene al menos un valor vacio o nulo en una celda requerida.'
  }

  return normalizedMessage
}

const DEFAULT_MASSIVE_UPLOAD_SOURCE = 'remuneration'
const MASSIVE_TEMPLATE_DOWNLOADS = {
  remuneration: {
    templateType: 'remuneracion',
    fileName: 'Remuneracion.xlsx'
  },
  buk: {
    templateType: 'buk',
    fileName: 'BUK.xlsx'
  }
}

const validateMassiveUploadExcel = (source, file) =>
  source === 'buk' ? validateBukExcel(file) : validateRemunerationExcel(file)

const downloadTemplateFile = (data, fileName) => {
  const url = window.URL.createObjectURL(new Blob([data]))
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const ModalAgregarRemuneracion = ({
  open,
  setOpen,
  setSwitchGetData
}) => {
  const [modoCarga, setModoCarga] = useState('masivo')
  const [massiveUploadSource, setMassiveUploadSource] = useState(DEFAULT_MASSIVE_UPLOAD_SOURCE)
  const [bukMonth, setBukMonth] = useState('')
  const [archivoExcel, setArchivoExcel] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [validationErrors, setValidationErrors] = useState([])
  const [individualData, setIndividualData] = useState({})
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const { ui, currentBusiness } = useContext(GlobalContext)
  const businessAPI = new BusinessAPI()

  useEffect(() => {
    if (!archivoExcel || modoCarga !== 'masivo') {
      setValidationErrors([])
      return
    }

    setErrorMessage(null)
    const fileError = getMassiveUploadFileError(massiveUploadSource, archivoExcel)
    if (fileError) {
      setValidationErrors([])
      setErrorMessage(fileError)
      return
    }

    validateMassiveUploadExcel(massiveUploadSource, archivoExcel).then(setValidationErrors)
  }, [archivoExcel, modoCarga, massiveUploadSource])

  const handleClose = () => {
    setModoCarga('masivo')
    setMassiveUploadSource(DEFAULT_MASSIVE_UPLOAD_SOURCE)
    setBukMonth('')
    setArchivoExcel(null)
    setErrorMessage(null)
    setValidationErrors([])
    setOpen(false)
  }

  const handleMassiveUploadSourceChange = (nextSource) => {
    setMassiveUploadSource(nextSource)
    setBukMonth('')
    setArchivoExcel(null)
    setErrorMessage(null)
    setValidationErrors([])
  }

  const buildBukUploadDate = (monthValue) => {
    if (!monthValue) return ''
    return `${currentYear}-${monthValue}-01`
  }

  const handleTemplateDownload = async (source) => {
    const template = MASSIVE_TEMPLATE_DOWNLOADS[source]
    if (!template || isDownloadingTemplate) return

    setIsDownloadingTemplate(true)

    try {
      const response = await businessAPI.downloadRemunerationTemplate(template.templateType)
      downloadTemplateFile(response.data, template.fileName)
    } catch (error) {
      console.error('Error al descargar plantilla:', error)
      sileo.error({
        title: 'Error de Descarga',
        description: 'No se pudo descargar la plantilla. Intenta nuevamente.'
      })
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const handleGuardar = async () => {
    if (modoCarga === 'masivo') {
      if (!archivoExcel) {
        setErrorMessage('Debe seleccionar un archivo')
        return
      }

      const fileError = getMassiveUploadFileError(massiveUploadSource, archivoExcel)
      if (fileError) {
        setErrorMessage(fileError)
        return
      }

      if (massiveUploadSource === 'buk' && !bukMonth) {
        setErrorMessage('Debe seleccionar un mes para la carga Buk')
        return
      }

      if (validationErrors.length > 0) {
        setErrorMessage(
          `El archivo contiene ${validationErrors.length} error${validationErrors.length > 1 ? 'es' : ''} de validacion. Corrija el archivo antes de continuar.`
        )
        return
      }

      setErrorMessage(null)

      try {
        const uploadRemunerationFile = async () => {
          const uploadFile =
            massiveUploadSource === 'buk'
              ? await getBukUploadFile(archivoExcel)
              : archivoExcel
          const formData = new FormData()
          formData.append('file', uploadFile)

          const response =
            massiveUploadSource === 'buk'
              ? await businessAPI.postBukUploadExcel(
                  currentBusiness.id,
                  formData,
                  buildBukUploadDate(bukMonth)
                )
              : await businessAPI.postUpload(currentBusiness.id, formData)
          const responseMessage = getRemunerationUploadRawMessage(response)

          if (isRemunerationUploadErrorMessage(responseMessage)) {
            throw new Error(responseMessage)
          }

          return response
        }

        await sileo.promise(uploadRemunerationFile, {
          loading: { title: 'Cargando Remuneraciones' },
          success: {
            title: 'Listo',
            description: (
              <span className='text-green-500/80 font-medium'>
                Remuneraciones cargadas exitosamente.
              </span>
            )
          },
          error: (error) => ({
            title: 'Error al Cargar',
            description: (
              <span className='whitespace-pre-wrap break-words text-red-500/80 font-medium'>
                {getRemunerationUploadErrorMessage(error)}
              </span>
            )
          })
        })

        setSwitchGetData(true)
        handleClose()
      } catch (error) {
        console.error('Error de carga:', error)
        setErrorMessage(getRemunerationUploadErrorMessage(error))
      }
    } else if (modoCarga === 'individual') {
      try {
        ui.setLoader({ visible: true })
        await businessAPI.postIndividual(currentBusiness.id, individualData)
        setSwitchGetData(true)
        handleClose()
      } catch (error) {
        console.error('Error al agregar comprobante:', error.message)
      } finally {
        ui.setLoader({ visible: false })
      }
    }
  }

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      footer={
        <>
          <Button
            onClick={handleClose}
            className='uppercase text-sm bg-primary text-white rounded-full'
            title='CERRAR'
          />
          <Button
            onClick={handleGuardar}
            className='uppercase text-sm bg-accent2 text-white rounded-full'
            title='GUARDAR'
          />
        </>
      }
    >
      <div className='relative w-full mx-auto'>
        <div className='flex items-center justify-between'>
          <Title className='text-2xl'>Agregar Remuneracion</Title>

          {modoCarga === 'masivo' && (
            <Button
              className='uppercase text-sm bg-accent2 text-white rounded-full'
              title='CARGA INDIVIDUAL'
              onClick={() => setModoCarga('individual')}
            />
          )}
          {modoCarga === 'individual' && (
            <Button
              className='uppercase text-sm bg-accent2 text-white rounded-full'
              title='CARGA MASIVA'
              onClick={() => setModoCarga('masivo')}
            />
          )}
        </div>

        <div className='mt-4'>
          {modoCarga === 'masivo' ? (
            <MasivaContent
              onFileSelect={setArchivoExcel}
              archivoExcel={archivoExcel}
              errorMessage={errorMessage}
              validationErrors={validationErrors}
              massiveUploadSource={massiveUploadSource}
              onMassiveUploadSourceChange={handleMassiveUploadSourceChange}
              bukMonth={bukMonth}
              isDownloadingTemplate={isDownloadingTemplate}
              onTemplateDownload={handleTemplateDownload}
              onBukMonthChange={(value) => {
                setBukMonth(value)
                setErrorMessage(null)
              }}
            />
          ) : (
            <IndividualContent onChangeData={setIndividualData} />
          )}
        </div>
      </div>
    </ResizableModal>
  )
}

export default ModalAgregarRemuneracion
